<template>
  <div class="d-flex align-center flex-wrap parameters-footer">
    <span v-if="version" class="text-caption">Parámetros versión {{ version }}</span>
    <v-spacer />
    <v-btn v-if="canRefresh" text small :loading="refreshing" @click="refresh">Actualizar parámetros</v-btn>

    <v-dialog v-model="errorsOpen" max-width="560">
      <v-card>
        <v-card-title class="text-h6">El Sheet tiene celdas para corregir</v-card-title>
        <v-card-text>
          <p class="text-body-2">No se cargó nada: las cotizaciones siguen usando la versión anterior.</p>
          <v-simple-table dense>
            <tbody>
              <tr v-for="error in errors" :key="error.cell + error.reason">
                <td class="font-weight-bold">{{ error.cell }}</td>
                <td>{{ reason(error.reason) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="errorsOpen = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="changesOpen" max-width="720" scrollable>
      <v-card>
        <v-card-title class="text-h6">Qué cambió en la versión {{ refreshedVersion }}</v-card-title>
        <v-card-text>
          <template v-if="explanationGroups.length">
            <div v-for="group in explanationGroups" :key="group.tab" class="parameters-footer__reason mb-4">
              <div class="pw-label mb-1">{{ group.tab }}</div>
              <p v-for="(item, index) in group.items" :key="index" class="text-body-2 mb-2">{{ item.text }}</p>
            </div>
          </template>
          <template v-else>
            <v-alert dense text type="warning" class="text-body-2">
              Nadie explicó estos cambios en la pestaña "Cambios" del Sheet. Abajo va el motivo que dice la nota de cada
              fila; la próxima vez agrega una fila en "Cambios" con la explicación.
            </v-alert>
            <div v-for="group in noteGroups" :key="group.reason" class="parameters-footer__reason mb-3">
              <p class="text-body-2 mb-0">{{ group.reason }}</p>
              <div class="text-caption">{{ group.count }} {{ group.count === 1 ? 'valor' : 'valores' }}</div>
            </div>
          </template>
          <p class="text-caption mt-2 mb-1">Las cotizaciones ya guardadas no cambian.</p>
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0">Ver el detalle ({{ changes.length }} valores)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Pestaña</th>
                      <th>Valor</th>
                      <th class="text-right">Antes</th>
                      <th class="text-right">Ahora</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="change in changes" :key="change.section + change.label">
                      <td class="parameters-footer__section">{{ change.section }}</td>
                      <td>{{ change.label }}</td>
                      <td class="text-right parameters-footer__value">{{ valueText(change.before, change.label) }}</td>
                      <td class="text-right parameters-footer__value font-weight-bold">{{ valueText(change.after, change.label) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="changesOpen = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import B2bQuotesService from '../services/b2b-quotes.service';
import { cellReason, errorMessage } from '../utils/b2b-quotes.utils';
import { changeValueText, noteReasons } from '../utils/parameter-changes.utils';

/**
 * Version of the pricing parameters in use and, for approvers, the button
 * that loads the Sheet again, and then what changed against the previous
 * version. Emits `refreshed` ({ version, changed }) after a load and `error`
 * (message) when it fails.
 */
export default {
  name: 'PricingParametersFooter',
  props: {
    version: { type: Number, default: null },
    canRefresh: { type: Boolean, default: false }
  },
  data() {
    return {
      refreshing: false,
      errors: [],
      errorsOpen: false,
      changes: [],
      explanations: [],
      changesOpen: false,
      refreshedVersion: null
    };
  },
  computed: {
    /** Why it changed, as written in the "Cambios" tab, grouped by tab in the order written. */
    explanationGroups() {
      const groups = [];
      for (const item of this.explanations) {
        let group = groups.find((g) => g.tab === item.tab);
        if (!group) groups.push((group = { tab: item.tab || 'General', items: [] }));
        group.items.push(item);
      }
      return groups;
    },
    /** Fallback when nobody wrote in "Cambios": the note of each changed row, with how many values it covers. */
    noteGroups() {
      return noteReasons(this.changes);
    }
  },
  methods: {
    async refresh() {
      this.refreshing = true;
      const res = await B2bQuotesService.refreshParameters();
      this.refreshing = false;
      if (res.status === 'ok') {
        this.$emit('refreshed', { version: res.data.version, changed: res.data.changed });
        this.changes = res.data.changes ?? [];
        this.explanations = res.data.explanations ?? [];
        this.refreshedVersion = res.data.version;
        this.changesOpen = this.changes.length > 0;
        return;
      }
      if (res.code === 'PRICING_SHEET_INVALID') {
        this.errors = res.details;
        this.errorsOpen = true;
        return;
      }
      this.$emit('error', errorMessage(res.code));
    },
    reason(code) {
      return cellReason(code);
    },
    valueText: changeValueText
  }
};
</script>

<style scoped>
.parameters-footer__reason {
  border-left: 3px solid var(--pw-primary-tint);
  border-radius: 0;
  padding-left: 12px;
}
.parameters-footer__summary {
  margin-top: 4px;
}
.parameters-footer__summary li {
  margin-bottom: 4px;
}
.parameters-footer__section {
  white-space: nowrap;
  color: var(--pw-text-muted);
}
.parameters-footer__value {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.parameters-footer {
  border-top: 1px solid var(--pw-border);
  margin-top: 32px;
  padding-top: 12px;
  color: var(--pw-text-muted);
}
</style>
